import { Fragment, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { formatTime, dayKey, dayLabel } from '../lib/format'
import { useFamilyData } from '../hooks/useFamilyData'
import type { BabyRecord, RecordType } from '../lib/types'
import { DIAPER_COLORS, DIAPER_TEXTURES } from '../lib/types'
import RecordDetail from './RecordDetail'

interface Props {
  aiVisionEnabled: boolean
}

const ICONS: Record<RecordType, { emoji: string; className: string; label: string }> = {
  pee: { emoji: '💧', className: 'pee', label: '嘘嘘' },
  poop: { emoji: '💩', className: 'poop', label: '臭臭' },
  mixed: { emoji: '✨', className: 'mixed', label: '混合' },
}

/**
 * 稳定 photo URL：同 photo_id 在 1hr 内复用同一签名 URL，
 * 避免 listRecords 每次都重新签名导致浏览器当新图重新拉。
 * Module-level 单例：RecordsList 和 RecordDetail 共享缓存。
 */
const photoUrlCache = new Map<string, { url: string; exp: number }>()

export function stablePhotoUrl(
  photoId: string | null | undefined,
  freshUrl: string | null | undefined
): string | null {
  if (!photoId || !freshUrl) return freshUrl ?? null
  const cached = photoUrlCache.get(photoId)
  if (cached && cached.exp > Date.now()) {
    return cached.url
  }
  try {
    const u = new URL(freshUrl, window.location.href)
    const exp = Number(u.searchParams.get('exp') || 0)
    if (exp > Date.now()) {
      photoUrlCache.set(photoId, { url: freshUrl, exp })
      return freshUrl
    }
  } catch {}
  return freshUrl
}

const MAX_DAYS = 7 // 最多显示 7 天
const MAX_PER_DAY = 12 // 每天最多显示 12 条

export default function RecordsList({ aiVisionEnabled }: Props) {
  const { records, setRecords, refresh } = useFamilyData()
  const [selected, setSelected] = useState<string | null>(null)

  // 按天分组（最近 7 天，每天最多 12 条）
  const grouped = useMemo(() => {
    const map = new Map<string, BabyRecord[]>()
    for (const r of records) {
      const k = dayKey(r.occurred_at)
      if (!map.has(k)) map.set(k, [])
      const arr = map.get(k)!
      if (arr.length < MAX_PER_DAY) arr.push(r)
    }
    // records 已按 occurred_at 倒序，map 保留顺序
    return Array.from(map.entries())
      .slice(0, MAX_DAYS)
      .map(([k, recs]) => ({ key: k, items: recs, firstTs: recs[0].occurred_at }))
  }, [records])

  const selectedRecord = selected ? records.find((r) => r.id === selected) ?? null : null
  const totalShown = grouped.reduce((s, g) => s + g.items.length, 0)
  const hasMore = records.length > totalShown

  function handleDeleted() {
    if (!selected) return
    setRecords(records.filter((r) => r.id !== selected))
    setSelected(null)
  }

  function handleDelete(r: { id: string }, e: React.MouseEvent) {
    e.stopPropagation()
    if (!confirm('确定删除这条记录？')) return
    // 乐观更新：先从本地移除
    setRecords(records.filter((x) => x.id !== r.id))
    api.deleteRecord(r.id).catch(() => {
      // 失败：触发后台 refresh 恢复
      refresh()
    })
  }

  return (
    <section className="records">
      <h2>最近记录</h2>
      {records.length === 0 ? (
        <div className="empty">还没有记录</div>
      ) : (
        <ul className="records-list">
          {grouped.map((g) => (
            <Fragment key={g.key}>
              <li className="record-day-header">
                <span className="record-day-label">{dayLabel(g.firstTs)}</span>
                <span className="record-day-count">
                  {g.items.length} 次{g.items.length === MAX_PER_DAY ? '+' : ''}
                </span>
              </li>
              {g.items.map((r) => {
                const icon = ICONS[r.type]
                return (
                  <li
                    key={r.id}
                    className="record-item clickable"
                    onClick={() => setSelected(r.id)}
                  >
                    <div className={`record-icon ${icon.className}`}>{icon.emoji}</div>
                    <div className="record-body">
                      <div className="record-type">
                        {icon.label}
                        {r.photo_id && <span className="has-photo"> 📷</span>}
                        {r.ai_summary && <span className="has-ai"> 🤖</span>}
                      </div>
                      <div className="record-time">{formatTime(r.occurred_at)}</div>
                      {(r.color || r.texture) && (
                        <div className="record-tags">
                          {r.color && (() => {
                            const c = DIAPER_COLORS.find((x) => x.value === r.color)
                            return c ? (
                              <span className="record-tag" title={c.label}>
                                <span className="record-tag-dot" style={{ background: c.cssColor }} />
                              </span>
                            ) : null
                          })()}
                          {r.texture && (() => {
                            const t = DIAPER_TEXTURES.find((x) => x.value === r.texture)
                            return t ? (
                              <span className="record-tag" title={t.label}>{t.emoji}</span>
                            ) : null
                          })()}
                        </div>
                      )}
                    </div>
                    {r.photo_signed_url && (
                      <img
                        className="record-thumb"
                        src={stablePhotoUrl(r.photo_id, r.photo_signed_url) ?? r.photo_signed_url}
                        alt=""
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                    <button
                      className="record-delete"
                      onClick={(e) => handleDelete(r, e)}
                      aria-label="删除"
                    >
                      ×
                    </button>
                  </li>
                )
              })}
            </Fragment>
          ))}
        </ul>
      )}
      {hasMore && (
        <div className="empty" style={{ marginTop: 8, padding: '20px 16px' }}>
          还有更早的 {records.length - totalShown} 条记录
        </div>
      )}
      {selectedRecord && (
        <RecordDetail
          record={selectedRecord}
          onClose={() => setSelected(null)}
          onDeleted={handleDeleted}
          aiVisionEnabled={aiVisionEnabled}
        />
      )}
    </section>
  )
}
